import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminFindSpacePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title: 'Find Your Perfect Workspace',
    hero_subtitle: 'Explore our premium locations and discover the ideal space for your business.',
    hero_image_url: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await apiClient.get('/find-space-page-content');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching find space page content:', error);
      toast.error('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/find-space-page-content', content);
      toast.success('Find Space page settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save page settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Find a Space Page Settings</h1>
          <p className="text-gray-600 mt-1">Configure the hero section of the Find a Space page</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Hero Section</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Hero Header Image</Label>
                <p className="text-sm text-gray-500 mb-2">Upload a banner image for the Find a Space page header. Recommended: Wide landscape image (1920x400px or similar)</p>
                <ImageUpload
                  value={content.hero_image_url}
                  onChange={(url) => setContent({ ...content, hero_image_url: url })}
                  label="Header Image"
                />
              </div>

              <div>
                <Label>Hero Title</Label>
                <Input
                  value={content.hero_title}
                  onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                  placeholder="Find Your Perfect Workspace"
                  required
                />
              </div>

              <div>
                <Label>Hero Subtitle</Label>
                <Textarea
                  value={content.hero_subtitle}
                  onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                  placeholder="Explore our premium locations and discover the ideal space for your business."
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
